import { Edge, isNode, Node, ReactFlowInstance } from "reactflow";
import {
  applyEdgeStyling,
  C_OFFSET_X,
  C_OFFSET_Y,
  contains,
  createColumnEdge,
  createColumnNode,
  createTableEdge,
  createTableNode,
  deleteIfExists,
  getColumnEdgeId,
  getColumnId,
  getHelperDataForCLL,
  getSeeMoreId,
  isColumn,
  isRefEdge,
  isNotColumn,
  T_LEVEL_SEPARATION,
  MAX_EXPAND_TABLE,
  F_OFFSET_X,
  F_OFFSET_Y,
  safeConcat,
  T_NODE_H,
  T_NODE_W,
  T_NODE_Y_SEPARATION,
  getColY,
  withinExclusive,
  isSeeMore,
  EdgeVisibility,
  ViewsTypes,
  applyNodeStyling,
  toggleModelEdges,
  toggleColumnEdges,
  LEVEL_SEPARATION_VERTICAL,
  T_NODE_Y_SEPARATION_VERTICAL,
  defaultEdgeStyle,
  createOpNode,
  highlightEdgeStyle,
  indirectHighlightEdgeStyle,
} from "./utils";
import { CollectColumn, ColumnLineage, Confidence, Details, SelectedColumn, Table } from "./types";
import { COLUMN_PREFIX } from "./constants";
import { TMoreTables } from "./components/MoreTables";
import { CLL } from "./cllUtils";
import { downstreamTables, getConnectedColumns, upstreamTables } from "./api";
import { NODE_TYPE_SHORTHAND } from "./components/Column";
import { Dispatch, ReducerWithoutAction } from "react";

const getConnectedTables = (right: boolean, table: string) =>
  right ? upstreamTables(table) : downstreamTables(table);
const calculateNewLevel = (right: boolean, i: number) => (right ? i + 1 : i - 1);

export const createNewNodesEdges = (
  nodes: Node[],
  edges: Edge[],
  tables: Table[],
  t: string,
  right: boolean,
  level: number,
  max_expand_table = MAX_EXPAND_TABLE,
  isVertical = false,
  details?: Details | null
) => {
  const newLevel = calculateNewLevel(right, level);

  const addUniqueEdge = (to: string, source?: string) => {
    const toLevel = nodes.find(n => n.id === to)?.data?.level || 0;
    const _edge = createTableEdge(level, toLevel, t, to, right, isVertical, source);
    const existingEdge = edges.find(e => e.id === _edge.id);
    if (!existingEdge) edges.push(_edge);
  };

  let tableAdded = 0;
  for (const _t of tables) {
    if (tableAdded >= max_expand_table) {
      const nodeId = getSeeMoreId(t, right);
      nodes.push({
        id: nodeId,
        data: { tables, prevTable: t, right, level: newLevel },
        position: { x: 100, y: 100 },
        type: "seeMore",
        width: T_NODE_W,
        height: 100,
      });
      addUniqueEdge(nodeId);
      break;
    }
    const existingNode = nodes.find(_n => _n.id === _t.table);
    if (!existingNode) {
      tableAdded++;
      if (!details) {
        nodes.push(createTableNode(_t, newLevel, t));
      } else {
        const opType = details[_t.table].type;
        if (opType in NODE_TYPE_SHORTHAND) {
          nodes.push(createTableNode(_t, newLevel, t));
        } else {
          nodes.push(createOpNode(_t.table, newLevel, t, details[_t.table]));
        }
      }
    }
    addUniqueEdge(_t.table, _t.source);
  }
};

export const layoutElementsOnCanvas = (nodes: Node[], edges: Edge[], isVertical = false) => {
  let minLevel = Infinity;
  let maxLevel = -Infinity;
  const tableWiseColumnCount: Record<string, number> = {};

  for (const n of nodes) {
    if (isColumn(n) && n.parentNode) {
      // assign position to columns earlier as they are relative to parent
      if (!(n.parentNode in tableWiseColumnCount)) {
        tableWiseColumnCount[n.parentNode] = 0;
      }

      n.position = {
        x: C_OFFSET_X,
        y: C_OFFSET_Y + getColY(tableWiseColumnCount[n.parentNode]),
      };
      tableWiseColumnCount[n.parentNode]++;
    } else {
      // calculate bounds along x axis
      const { level } = n.data;
      minLevel = Math.min(minLevel, level);
      maxLevel = Math.max(maxLevel, level);
    }
  }

  const tableWiseLevelColumnCount: Record<string, number> = {};
  const levelWiseTables: Record<number, string[]> = {};
  const tableWiseLevelPos: Record<string, number> = {};
  const visited: Record<string, boolean> = {};

  // create neightbours list for convenience
  const adjacencyListRight: Record<string, string[]> = {};
  const adjacencyListLeft: Record<string, string[]> = {};
  for (const e of edges) {
    if (isColumn(e)) continue;
    if (
      isSeeMore(nodes.find(x => x.id === e.source)!) ||
      isSeeMore(nodes.find(x => x.id === e.target)!)
    )
      continue;
    adjacencyListRight[e.source] = adjacencyListRight[e.source] || [];
    adjacencyListRight[e.source].push(e.target);
    adjacencyListLeft[e.target] = adjacencyListLeft[e.target] || [];
    adjacencyListLeft[e.target].push(e.source);
  }

  // calculate metadata such as tables and columns per level
  // to get tables position along y axis
  const processNode = (n: Node) => {
    const { level } = n.data;
    levelWiseTables[level] = levelWiseTables[level] || [];
    if (!levelWiseTables[level].includes(n.id)) {
      tableWiseLevelPos[n.id] = levelWiseTables[level].length;
      tableWiseLevelColumnCount[n.id] = 0;
      for (const curr of levelWiseTables[level])
        tableWiseLevelColumnCount[n.id] += tableWiseColumnCount[curr] || 0;
      levelWiseTables[level].push(n.id);
    }
  };
  const dfs = (n: string, adjacencyList: Record<string, string[]>) => {
    if (visited[n]) return;
    visited[n] = true;
    processNode(nodes.find(item => item.id === n)!);
    for (const m of adjacencyList[n] || []) dfs(m, adjacencyList);
  };

  for (const n of nodes) {
    if (isColumn(n)) continue;
    if (isSeeMore(n)) continue;
    if (visited[n.id]) continue;
    dfs(n.id, adjacencyListRight);
    visited[n.id] = false;
    dfs(n.id, adjacencyListLeft);
  }
  // will place see more node at the end of level
  for (const n of nodes) {
    if (isColumn(n)) continue;
    if (!isSeeMore(n)) continue;
    processNode(n);
  }

  // assign position to table and see more nodes
  const getY = (n: Node) => {
    const _index = tableWiseLevelPos[n.id] || 0;
    const _columnCount = tableWiseLevelColumnCount[n.id] || 0;
    return F_OFFSET_Y + _index * (T_NODE_H + T_NODE_Y_SEPARATION) + getColY(_columnCount, _index);
  };

  const getX = (level: number) => {
    const basisLevel = level - minLevel;
    return basisLevel * (T_NODE_W + T_LEVEL_SEPARATION) + F_OFFSET_X;
  };

  const getYVertical = (level: number) => {
    const basisLevel = level - minLevel;
    return basisLevel * (T_NODE_H + LEVEL_SEPARATION_VERTICAL) + F_OFFSET_Y;
  };

  const getXVertical = (n: Node) => {
    const _index = tableWiseLevelPos[n.id] || 0;
    const _columnCount = tableWiseLevelColumnCount[n.id] || 0;
    return (
      F_OFFSET_Y +
      _index * (T_NODE_H + T_NODE_Y_SEPARATION_VERTICAL) +
      getColY(_columnCount, _index)
    );
  };

  for (const n of nodes) {
    if (isColumn(n)) continue;
    const { level } = n.data;
    n.position = isVertical
      ? { x: getXVertical(n), y: getYVertical(level) }
      : { x: getX(level), y: getY(n) };
  }
};

/**
 * Reconcile column-row canvas nodes against the user's `expandedTables`
 * preference. Adds column nodes for tables marked expanded that don't yet
 * have rows on the canvas; removes column nodes for tables that are no
 * longer expanded *unless* those rows came from column-level lineage
 * (which writes to `collectColumns`). The protected-by-CLL rule keeps the
 * generic "Show columns" toggle from clobbering an in-progress CLL session.
 */
export const applyTableExpansion = (
  nodes: Node[],
  edges: Edge[],
  expandedTables: Record<string, boolean>,
  collectColumns: Record<string, CollectColumn[]>,
  tableMetadata: Map<string, { columns: string[]; nodeType: string }>
): [Node[], Edge[]] => {
  const existingColumnsByTable = new Map<string, Set<string>>();
  for (const n of nodes) {
    if (!isColumn(n) || !n.parentNode) continue;
    if (!existingColumnsByTable.has(n.parentNode)) {
      existingColumnsByTable.set(n.parentNode, new Set());
    }
    // n.id format: "column-${table}/${col}"
    const colName = n.id.slice(COLUMN_PREFIX.length + n.parentNode.length + 1);
    existingColumnsByTable.get(n.parentNode)!.add(colName);
  }

  const cllProtected = (table: string, col: string) =>
    Boolean(collectColumns[table]?.some(c => c.column === col));

  const additions: Node[] = [];
  const removals = new Set<string>();

  // Add columns for newly-expanded tables.
  for (const table in expandedTables) {
    if (!expandedTables[table]) continue;
    const meta = tableMetadata.get(table);
    if (!meta) continue;
    const existing = existingColumnsByTable.get(table) ?? new Set<string>();
    for (const col of meta.columns) {
      if (existing.has(col)) continue;
      additions.push(createColumnNode(table, col, undefined, {}, meta.nodeType));
    }
  }

  // Remove columns for tables that were expanded but now aren't (and
  // weren't put there by CLL).
  for (const [table, cols] of existingColumnsByTable) {
    if (expandedTables[table]) continue;
    for (const col of cols) {
      if (cllProtected(table, col)) continue;
      removals.add(getColumnId(table, col));
    }
  }

  if (additions.length === 0 && removals.size === 0) {
    return [nodes, edges];
  }

  const filtered = nodes.filter(n => !removals.has(n.id));
  const next = [...filtered, ...additions];

  // Drop edges that referenced removed column nodes — ReactFlow throws on
  // dangling edges during layout/render. This covers CLL column-edges that
  // happened to source/target the removed rows, and any ref edges that
  // were column-anchored before the collapse.
  const nextEdges =
    removals.size === 0
      ? edges
      : edges.filter(e => !removals.has(e.source) && !removals.has(e.target));

  // Re-layout so positions reflect added/removed column rows.
  layoutElementsOnCanvas(next, nextEdges);
  return [next, nextEdges];
};

export const resetTableHighlights = (nodes: Node[], edges: Edge[]): [Node[], Edge[]] => {
  nodes.forEach(n => applyNodeStyling(n, true));
  edges.forEach(e => applyEdgeStyling(e, false));
  return [nodes, edges];
};

export const highlightTableConnections = (
  nodes: Node[],
  edges: Edge[],
  table: string
): [Node[], Edge[]] => {
  // Model edges might be hidden when column lineage was selected
  // hide column lineage edges and show all other edges
  toggleModelEdges(edges, true);
  toggleColumnEdges(edges, false);

  const highlightNode: Record<string, boolean> = {};
  const highlightEdge: Record<string, boolean> = {};
  const bfsTraversal = (src: "source" | "target", dst: "source" | "target") => {
    const queue = [table];
    const visited: Record<string, boolean> = {};
    while (queue.length > 0) {
      const curr = queue.shift()!;
      visited[curr] = true;
      highlightNode[curr] = true;
      edges.forEach(e => {
        if (e[src] === curr) {
          highlightEdge[e.id] = true;
          if (!visited[e[dst]]) queue.push(e[dst]);
        }
      });
    }
  };
  bfsTraversal("source", "target");
  bfsTraversal("target", "source");

  // apply styling
  const newEdges = [...edges];
  newEdges.forEach(_e => applyEdgeStyling(_e, highlightEdge[_e.id]));

  const newNodes = [...nodes];
  newNodes.forEach(_n => applyNodeStyling(_n, Boolean(highlightNode[_n.id])));

  return [newNodes, newEdges];
};

export const setHighlightedTableConnections = (flow: ReactFlowInstance, table: string) => {
  const _nodes = flow.getNodes();
  const _edges = flow.getEdges();
  const [nodes, edges] = highlightTableConnections(_nodes, _edges, table);
  flow.setNodes(nodes);
  flow.setEdges(edges);
};

// Adjacency over model (non-column) nodes; edges are treated as undirected.
// Exported so callers that probe several nodes against the same graph (e.g. the
// collapse-handle check in CustomNodes) can build it once and reuse it instead
// of paying an O(V + E) rebuild per node.
export const buildModelAdjacency = (nodes: Node[], edges: Edge[]): Record<string, string[]> => {
  const adjacency: Record<string, string[]> = {};
  for (const n of nodes) {
    if (isColumn(n)) continue;
    adjacency[n.id] = [];
  }
  for (const e of edges) {
    if (isColumn(e)) continue;
    if (adjacency[e.source]) adjacency[e.source].push(e.target);
    if (adjacency[e.target]) adjacency[e.target].push(e.source);
  }
  return adjacency;
};

// Extend `removeSet` with any model node that can no longer reach `root` once
// `removeSet` is gone — orphans left dangling by the removal, e.g. siblings whose
// only path to the root ran through a collapsed parent.
const addOrphanedNodes = (
  adjacency: Record<string, string[]>,
  root: string,
  removeSet: Set<string>
): void => {
  const reachable = new Set<string>([root]);
  const queue = [root];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    for (const next of adjacency[curr] || []) {
      if (removeSet.has(next) || reachable.has(next)) continue;
      reachable.add(next);
      queue.push(next);
    }
  }
  for (const id of Object.keys(adjacency)) {
    if (id !== root && !reachable.has(id) && !removeSet.has(id)) removeSet.add(id);
  }
};

// Model nodes (ids) that a collapse of `table` toward `right` should remove,
// while keeping the lineage root and everything reachable from it that does NOT
// have to pass through `table`. This prunes the sibling branches hanging off a
// shared parent without dropping the root (the open-file node) or its own
// lineage, and it leaves diamond-connected nodes in place.
const computeBranchRemoval = (
  nodes: Node[],
  edges: Edge[],
  table: string,
  root: string,
  right: boolean,
  prebuiltAdjacency?: Record<string, string[]>
): Set<string> => {
  const node = nodes.find(n => n.id === table);
  if (!node) return new Set();
  const level = node.data.level;

  const levelOf: Record<string, number> = {};
  for (const n of nodes) {
    if (isColumn(n)) continue;
    levelOf[n.id] = n.data.level;
  }
  const adjacency = prebuiltAdjacency ?? buildModelAdjacency(nodes, edges);

  // Nodes connected to the root without routing through `table` are protected.
  const protectedSet = new Set<string>([root]);
  const protectQueue = [root];
  while (protectQueue.length > 0) {
    const curr = protectQueue.shift()!;
    if (curr === table) continue; // barrier: don't expand past the clicked node
    for (const next of adjacency[curr] || []) {
      if (!protectedSet.has(next)) {
        protectedSet.add(next);
        protectQueue.push(next);
      }
    }
  }
  protectedSet.add(table);

  // Walk outward from `table` in the chosen direction; remove what isn't protected.
  const removeSet = new Set<string>();
  const seen = new Set<string>([table]);
  const queue = [table];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    for (const next of adjacency[curr] || []) {
      if (seen.has(next)) continue;
      const beyond = right ? levelOf[next] > level : levelOf[next] < level;
      if (!beyond) continue;
      seen.add(next);
      queue.push(next);
      if (!protectedSet.has(next)) removeSet.add(next);
    }
  }

  // Also drop nodes orphaned by the removal (e.g. siblings that only reached the
  // root through the parent being collapsed).
  addOrphanedNodes(adjacency, root, removeSet);

  return removeSet;
};

// True when collapsing `table` toward `right` would remove at least one node —
// used to hide a collapse handle that would otherwise be a no-op.
export const hasCollapsibleBranch = (
  nodes: Node[],
  edges: Edge[],
  table: string,
  root: string,
  right: boolean,
  prebuiltAdjacency?: Record<string, string[]>
): boolean => computeBranchRemoval(nodes, edges, table, root, right, prebuiltAdjacency).size > 0;

// Reconcile the graph to a level band [lowerLevel, upperLevel] around the root,
// used by the Expand popover so lowering the chosen levels prunes the outer
// nodes. Unlike a directional collapse it only drops nodes outside the band and
// their dangling edges, so the root's in-band connections are never severed.
export const pruneToLevelBand = (
  prevNodes: Node[],
  prevEdges: Edge[],
  root: string,
  lowerLevel: number,
  upperLevel: number
): [Node[], Edge[]] => {
  const removeSet = new Set<string>();
  for (const n of prevNodes) {
    if (isColumn(n) || n.id === root) continue;
    const level = n.data.level;
    if (level < lowerLevel || level > upperLevel) removeSet.add(n.id);
  }
  if (removeSet.size === 0) return [prevNodes, prevEdges];

  // Drop nodes orphaned once the out-of-band nodes are gone — e.g. siblings at
  // the root's own level that were only connected through a removed parent.
  addOrphanedNodes(buildModelAdjacency(prevNodes, prevEdges), root, removeSet);

  const columnRemove = new Set<string>();
  for (const n of prevNodes) {
    if (n.parentNode && removeSet.has(n.parentNode)) columnRemove.add(n.id);
  }

  const dropped = (id: string) => removeSet.has(id) || columnRemove.has(id);
  const newNodes = prevNodes.filter(n => !dropped(n.id));
  const newEdges = prevEdges.filter(e => !dropped(e.source) && !dropped(e.target));
  return [newNodes, newEdges];
};

// Collapse a node's branch in one direction, preserving the root and its lineage.
export const collapseBranch = (
  prevNodes: Node[],
  prevEdges: Edge[],
  table: string,
  root: string,
  right: boolean
): [Node[], Edge[]] => {
  const removeSet = computeBranchRemoval(prevNodes, prevEdges, table, root, right);
  if (removeSet.size === 0) return [prevNodes, prevEdges];

  const columnRemove = new Set<string>();
  for (const n of prevNodes) {
    if (n.parentNode && removeSet.has(n.parentNode)) columnRemove.add(n.id);
  }

  const dropped = (id: string) => removeSet.has(id) || columnRemove.has(id);
  const newNodes = prevNodes.filter(n => !dropped(n.id));
  const newEdges = prevEdges.filter(e => !dropped(e.source) && !dropped(e.target));
  return [newNodes, newEdges];
};

const processColumnLineage = async (
  prevNodes: Node[],
  levelMap: Record<string, number>,
  seeMoreIdTableReverseMap: Record<string, string>,
  tableNodes: Record<string, boolean>,
  curr: [string, string][],
  right: boolean,
  currAnd1HopTables: string[],
  selectedColumn: SelectedColumn,
  edgeVisibility: EdgeVisibility,
  setErrors: Dispatch<ReducerWithoutAction<Record<string, string[]>>>
) => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const { column_lineage, confidence, errors } = await getConnectedColumns({
    targets: curr,
    upstreamExpansion: right,
    currAnd1HopTables,
    selectedColumn,
    showIndirectEdges: edgeVisibility["indirect"],
  });
  setErrors(prev => ({ ...prev, ...errors }));
  CLL.addLinks(column_lineage.length);
  const columnLineage = column_lineage.filter(e =>
    right ? contains(curr, e.source) : contains(curr, e.target)
  );
  const newCurr = columnLineage.map(e => (right ? e.target : e.source));
  const collectColumns: Record<string, CollectColumn[]> = {};

  const addToCollectColumns = ([_table, _column]: [string, string], viewsType?: ViewsTypes) => {
    collectColumns[_table] = collectColumns[_table] || [];
    const _existingColumn = collectColumns[_table].find(c => c.column === _column);
    if (!_existingColumn) {
      collectColumns[_table].push({ column: _column, viewsType });
    } else if (viewsType === "Transformation") {
      _existingColumn.viewsType = viewsType;
    }
  };

  const addToEdges = (id1: string, id2: string, source: string, target: string, type: string) => {
    const id = getColumnEdgeId(source, target);
    if (edges.find(e => e.id === id)) return;
    edges.push(
      createColumnEdge(source, target, levelMap[id1], levelMap[id2], type, edgeVisibility)
    );
  };

  const seeMoreLineage: ColumnLineage[] = [];
  for (const e of columnLineage) {
    addToCollectColumns(e.source);
    addToCollectColumns(e.target, e.viewsType);
    const [t0] = e.source;
    const [t1] = e.target;

    const sourceTableExist = tableNodes[t0];
    const targetTableExist = tableNodes[t1];
    const sourceId = e.source.join("/");
    const targetId = e.target.join("/");
    const source = COLUMN_PREFIX + sourceId;
    const target = COLUMN_PREFIX + targetId;

    const edgeType = e.type;
    if (sourceTableExist && targetTableExist) {
      addToEdges(t0, t1, source, target, edgeType);
    } else if (sourceTableExist) {
      const seeMoreId = seeMoreIdTableReverseMap[t1];
      addToEdges(t0, seeMoreId, source, seeMoreId, edgeType);
      seeMoreLineage.push(e);
    } else if (targetTableExist) {
      const seeMoreId = seeMoreIdTableReverseMap[t0];
      addToEdges(seeMoreId, t1, seeMoreId, target, edgeType);
      seeMoreLineage.push(e);
    } else {
      seeMoreLineage.push(e);
      // TODO: check is nothing to do in this case
    }
  }

  for (const t in collectColumns) {
    if (!tableNodes[t]) continue;
    const arr = [...collectColumns[t]];
    arr.sort((a, b) => a.column.localeCompare(b.column));
    for (const c of arr) {
      const viewsCodes: Record<string, [string, string][]> = {};
      columnLineage
        .filter(e => e.target.join("/") === `${t}/${c.column}`)
        .forEach(e => {
          if (e.type === "indirect") return;
          viewsCodes[e.source.join("/")] = e.viewsCode || [];
        });
      nodes.push(
        createColumnNode(
          t,
          c.column,
          c.viewsType,
          viewsCodes,
          prevNodes.find(n => (n.id = t))?.data?.nodeType
        )
      );
    }
  }

  return { nodes, edges, collectColumns, newCurr, confidence, seeMoreLineage };
};

export const staticProcessColumnLineage = async (
  _nodes: Node[],
  _edges: Edge[],
  column_rk: string,
  postConnectedColumns: (args: {
    column_fqn: string;
    edges: { src: string; dst: string }[];
  }) => Promise<{
    collect_columns: Record<string, CollectColumn[]>;
    highlight_edges: [string, string, string][];
  }>
) => {
  let nodes = _nodes.filter(isNotColumn);
  let edges = _edges.filter(isNotColumn);
  [nodes, edges] = resetTableHighlights(nodes, edges);

  const levelMap: Record<string, number> = {};
  nodes.forEach(n => (levelMap[n.id] = n.data.level));

  const tableNodes: Record<string, boolean> = {};
  _nodes.filter(_n => _n.type === "table").forEach(_n => (tableNodes[_n.id] = true));
  const seeMoreIdTableReverseMap: Record<string, string> = {};
  const edgesPayload = [];
  for (const e of _edges) {
    if (e.id.startsWith(COLUMN_PREFIX)) continue;
    const sourceTableExist = tableNodes[e.source];
    const targetTableExist = tableNodes[e.target];
    if (sourceTableExist && targetTableExist) {
      edgesPayload.push({ src: e.source, dst: e.target });
    } else if (sourceTableExist) {
      const _n = _nodes.find(_n => _n.id === e.target)!;
      _n.data.tables?.forEach((_t: Table) => {
        edgesPayload.push({ src: e.source, dst: _t.table });
        seeMoreIdTableReverseMap[_t.table] = e.target;
      });
    } else if (targetTableExist) {
      const _n = _nodes.find(_n => _n.id === e.source)!;
      _n.data.tables?.forEach((_t: Table) => {
        edgesPayload.push({ src: _t.table, dst: e.target });
        seeMoreIdTableReverseMap[_t.table] = e.source;
      });
    } else {
      // TODO: check is nothing to do in this case
    }
  }

  const { collect_columns, highlight_edges } = await postConnectedColumns({
    column_fqn: column_rk,
    edges: edgesPayload,
  });

  for (const t in collect_columns) {
    if (!tableNodes[t]) continue;
    const arr = [...collect_columns[t]];
    arr.sort((a, b) => a.column.localeCompare(b.column));
    for (const c of arr) {
      const nodeType = _nodes.find(n => n.id === t)?.data?.nodeType;
      nodes.push(createColumnNode(t, c.column, c.viewsType, {}, nodeType));
    }
  }

  edges.forEach(_e => (_e.style = defaultEdgeStyle));
  const addToEdges = (
    id1: string,
    id2: string,
    source: string,
    target: string,
    edgeType: string
  ) => {
    const id = getColumnEdgeId(source, target);
    if (edges.find(e => e.id === id)) return;
    edges.push(
      createColumnEdge(source, target, levelMap[id1], levelMap[id2], edgeType, {
        direct: edgeType === "direct",
        indirect: edgeType === "indirect",
      })
    );
  };
  const seeMoreLineage = [];

  for (const e of highlight_edges) {
    const [t0] = e[0].split("/");
    const [t1] = e[1].split("/");
    const sourceTableExist = tableNodes[t0];
    const targetTableExist = tableNodes[t1];
    const source = COLUMN_PREFIX + e[0];
    const target = COLUMN_PREFIX + e[1];
    const edgeType = e[2] || "direct";
    if (sourceTableExist && targetTableExist) {
      addToEdges(t0, t1, source, target, edgeType);
    } else if (sourceTableExist) {
      const seeMoreId = seeMoreIdTableReverseMap[t1];
      addToEdges(t0, seeMoreId, source, seeMoreId, edgeType);
      seeMoreLineage.push(e);
    } else if (targetTableExist) {
      const seeMoreId = seeMoreIdTableReverseMap[t0];
      addToEdges(seeMoreId, t1, seeMoreId, target, edgeType);
      seeMoreLineage.push(e);
    } else {
      seeMoreLineage.push(e);
      // TODO: check is nothing to do in this case
    }
  }

  layoutElementsOnCanvas(nodes, edges);

  return { nodes, edges, collect_columns };
};

const mergeNodesEdges = (
  prevState: { nodes: Node[]; edges: Edge[] },
  patchState: { nodes: Node[]; edges: Edge[] },
  selectedColumn: SelectedColumn,
  right: boolean
): [Node[], Edge[]] => {
  const nodes = [...prevState.nodes];
  const edges = [...prevState.edges];
  patchState.nodes.forEach(n => {
    const existing = nodes.find(x => x.id === n.id);
    if (!existing) {
      nodes.push(n);
    } else {
      const viewsCode =
        n.data.viewsCode && Object.keys(n.data.viewsCode).length
          ? n.data.viewsCode
          : existing.data.viewsCode;
      existing.data = {
        ...existing.data,
        ...n.data,
        viewsCode,
        viewsType: existing.data.viewsType || n.data.viewsType,
      };
    }
  });

  patchState.edges.forEach(e => {
    if (edges.find(e1 => e1.id === e.id)) return;
    edges.push(e);
  });

  if (selectedColumn.name) {
    const edgeType: Record<string, string> = {};
    const start = getColumnId(selectedColumn.table, selectedColumn.name);
    const columnEdgeType: Record<string, string> = { [start]: "direct" };
    const queue = [start];
    const visited: Record<string, boolean> = {};
    const src = right ? "source" : "target";
    const dst = !right ? "source" : "target";
    while (queue.length > 0) {
      const curr = queue.shift()!;
      if (visited[curr]) continue;
      visited[curr] = true;
      // 1st pass to collect which type of edge comes towards target column
      for (const e of edges) {
        if (e[src] !== curr) continue;
        queue.push(e[dst]);
        if (columnEdgeType[e[dst]] === "direct") continue;
        columnEdgeType[e[dst]] = columnEdgeType[e[src]] === "direct" ? e.data.type : "indirect";
      }
      // 2nd pass to assign type to edge
      for (const e of edges) {
        if (e[src] !== curr) continue;
        edgeType[e.id] = columnEdgeType[e[dst]];
      }
    }
    for (const e of edges) {
      if (isNotColumn(e)) continue;
      e.data.type = edgeType[e.id] || e.data.type;
      e.style = e.data.type === "direct" ? highlightEdgeStyle : indirectHighlightEdgeStyle;
    }
  }

  layoutElementsOnCanvas(nodes, edges);
  return [nodes, edges];
};

export const removeColumnNodes = (_nodes: Node[], _edges: Edge[]): [Node[], Edge[]] => {
  const nodes = _nodes.filter(n => isNotColumn(n));
  const edges = _edges.filter(n => isNotColumn(n));
  return [nodes, edges];
};

export const expandTableLineage = async (
  _nodes: Node[],
  _edges: Edge[],
  table: string,
  right: boolean
): Promise<[Node[], Edge[]]> => {
  const nodes = [..._nodes];
  const edges = [..._edges];
  const queue: { table: string; level: number }[] = [
    { table, level: nodes.find(n => n.id === table)!.data.level },
  ];
  const visited: Record<string, boolean> = {};
  while (queue.length > 0) {
    const { table, level } = queue.shift()!;
    if (visited[table]) continue;
    visited[table] = true;
    const { tables } = await getConnectedTables(right, table);
    createNewNodesEdges(nodes, edges, tables, table, right, level);
    tables.forEach(t => {
      const _t = nodes.find(n => n.id === t.table);
      if (_t?.data.materialization === "ephemeral") {
        queue.push({ table: t.table, level: _t.data.level });
      }
    });
  }
  return [nodes, edges];
};

export const expandTableLineageLevelWise = async (
  _nodes: Node[],
  _edges: Edge[],
  _table: string,
  lb: number, // lower bound
  ub: number, // upper bound
  max_expand_table: number = MAX_EXPAND_TABLE
): Promise<[Node[], Edge[]]> => {
  const nodes = [..._nodes];
  const edges = [..._edges];
  if (lb >= ub) return [nodes, edges];
  const withinExcBounds = withinExclusive(lb, ub);
  const rootLevel = nodes.find(n => n.id === _table)!.data.level;
  const bfs = async (right: boolean) => {
    const queue: { table: string; level: number }[] = [{ table: _table, level: rootLevel }];
    const visited: Record<string, boolean> = {};
    while (queue.length > 0) {
      const curr = queue.shift()!;
      if (visited[curr.table]) continue;
      visited[curr.table] = true;
      const { tables = [] } = await getConnectedTables(right, curr.table);
      createNewNodesEdges(nodes, edges, tables, curr.table, right, curr.level, max_expand_table);
      const newLevel = calculateNewLevel(right, curr.level);
      if (withinExcBounds(newLevel)) {
        queue.push(...tables.map(t => ({ table: t.table, level: newLevel })));
      } else {
        queue.push(
          ...tables
            .filter(t => t.materialization === "ephemeral")
            .map(t => ({ table: t.table, level: newLevel }))
        );
      }
    }
  };
  if (ub > rootLevel) await bfs(true);
  if (lb < rootLevel) await bfs(false);

  return [nodes, edges];
};

const _calculateMinLevel = (
  nodes: Node[],
  edges: Edge[],
  table: string,
  right: boolean
): number => {
  if (!table) return -1;
  const src = right ? "source" : "target";
  const dst = right ? "target" : "source";
  const countKey = right ? "upstreamCount" : "downstreamCount";

  const nodesMap: Record<string, Node> = {};
  const adjacencyList: Record<string, string[]> = {};
  for (const n of nodes) {
    if (isColumn(n)) continue;
    nodesMap[n.id] = n;
    adjacencyList[n.id] = [];
  }
  for (const e of edges) {
    if (isColumn(e)) continue;
    adjacencyList[e[src]].push(e[dst]);
  }

  // get the node with lowest level which can be expanded but not yet expanded
  const bfs = () => {
    const queue = [table];
    const visited: Record<string, boolean> = {};
    while (queue.length > 0) {
      const curr = queue.shift()!;
      if (visited[curr]) continue;
      visited[curr] = true;
      const nodeData = nodesMap[curr].data;
      if (nodeData[countKey] === 0) continue;
      if (adjacencyList[curr].length < nodeData[countKey]) return curr;
      for (const n of adjacencyList[curr]) queue.push(n);
    }
  };
  const targetNode = bfs();

  // unable to find such node means everything is already expanded
  if (!targetNode) return -1;
  const { level: rootLevel } = nodesMap[table].data;
  const { level: targetLevel } = nodesMap[targetNode].data;
  return right ? targetLevel - rootLevel : rootLevel - targetLevel;
};

export const calculateMinLevel = (
  nodes: Node[],
  edges: Edge[],
  table: string
): [number, number] => [
  _calculateMinLevel(nodes, edges, table, false),
  _calculateMinLevel(nodes, edges, table, true),
];

export const bfsTraversal = async (
  nodes: Node[],
  edges: Edge[],
  right: boolean,
  columns: { name: string; table: string }[],
  setConfidence: (operatorList: Confidence["operator_list"]) => void,
  setMoreTables: (moreTables: ColumnLineage[]) => void,
  mergeCollectColumns: (newCollectColumns: Record<string, CollectColumn[]>) => void,
  getNodesEdges: () => [Node[], Edge[]],
  setNodesEdges: (ns: Node[], es: Edge[]) => void,
  selectedColumn: SelectedColumn,
  edgeVisibility: EdgeVisibility,
  setErrors: Dispatch<ReducerWithoutAction<Record<string, string[]>>>
): Promise<boolean> => {
  let isLineage = false;
  // creating helper data for current lineage once
  const { levelMap, tableNodes, seeMoreIdTableReverseMap } = getHelperDataForCLL(nodes, edges);

  const _getNode = (id: string) => nodes.find(n => n.id === id);

  const visited: Record<string, boolean> = {};
  const ephemeralAncestors: Record<string, [string, string][]> = {};
  let currTargetColumns: [string, string][] = columns.map(c => [c.table, c.name]);
  let currEphemeralNodes: string[] = [];
  setErrors(() => ({}));
  while (true as boolean) {
    if (CLL.isCancelled) break;
    currTargetColumns = currTargetColumns.filter(x => !visited[x.join("/")]);
    if (currTargetColumns.length === 0 && currEphemeralNodes.length === 0) {
      break;
    }
    const currTargetTables: Record<string, boolean> = {};
    currTargetColumns.forEach(x => {
      visited[x.join("/")] = true;
      currTargetTables[x[0]] = true;
    });

    const [src, dst]: ("source" | "target")[] = right ? ["source", "target"] : ["target", "source"];
    const hop1Tables: string[] = [];
    const _currEphemeralNodes: string[] = [];
    const collectEphemeralAncestors: string[] = [];
    let noDependents = false;
    for (const e of edges) {
      if (isColumn(e)) continue;
      const srcTable = e[src];
      const dstNode = e[dst];
      const dstTables = tableNodes[dstNode]
        ? [_getNode(dstNode)?.data as Table]
        : (_getNode(dstNode)?.data as TMoreTables)?.tables?.filter(t => !tableNodes[t.table]);
      dstTables?.forEach(dstTableData => {
        if (!dstTableData) return;
        const { table: dstTable, materialization } = dstTableData;
        if (currTargetTables[srcTable]) {
          noDependents = true;
          if (materialization === "ephemeral") {
            // carry forward
            safeConcat(
              ephemeralAncestors,
              dstTable,
              currTargetColumns.filter(c => c[0] === srcTable)
            );
            _currEphemeralNodes.push(dstTable);
          } else {
            hop1Tables.push(dstTable);
          }
        } else if (currEphemeralNodes.includes(srcTable)) {
          noDependents = true;
          if (materialization === "ephemeral") {
            // carry forward follow through
            safeConcat(ephemeralAncestors, dstTable, ephemeralAncestors[srcTable]);
            _currEphemeralNodes.push(dstTable);
          } else {
            collectEphemeralAncestors.push(srcTable);
            hop1Tables.push(dstTable);
          }
        }
      });
    }
    if (!noDependents) {
      break;
    }
    currEphemeralNodes = _currEphemeralNodes;

    const currAnd1HopTables = Object.keys(currTargetTables).concat(hop1Tables);

    collectEphemeralAncestors.forEach(t => {
      currTargetColumns.push(...ephemeralAncestors[t]);
      currAnd1HopTables.push(...ephemeralAncestors[t].map(c => c[0]));
    });
    const patchState = await processColumnLineage(
      nodes,
      levelMap,
      seeMoreIdTableReverseMap,
      tableNodes,
      currTargetColumns,
      right,
      Array.from(new Set(currAnd1HopTables)),
      selectedColumn,
      edgeVisibility,
      setErrors
    );
    if (patchState.confidence?.confidence === "low") {
      setConfidence(patchState.confidence?.operator_list || []);
    }
    currTargetColumns = patchState.newCurr;
    if (!isLineage && currTargetColumns.length > 0) {
      isLineage = true;
    }
    currTargetColumns = currTargetColumns.filter(
      c => edges.filter(e => (right ? e.source : e.target) === c[0]).length > 0
    );
    const [ns, es] = getNodesEdges();
    const [_nodes, _edges] = mergeNodesEdges(
      { nodes: ns, edges: es },
      patchState,
      selectedColumn,
      right
    );

    setMoreTables(patchState.seeMoreLineage);

    layoutElementsOnCanvas(_nodes, _edges);
    setNodesEdges(_nodes, _edges);
    mergeCollectColumns(patchState.collectColumns);
  }
  return isLineage;
};

export const moveTableFromSeeMoreToCanvas = (
  nodes: Node[],
  edges: Edge[],
  _table: Table,
  { prevTable, tables, right, level, lineage }: TMoreTables,
  edgeVisibility: EdgeVisibility
): boolean => {
  const { table } = _table;
  const node = nodes.find(n => n.id === table);
  if (node) return false;
  nodes.push(createTableNode(_table, level!, prevTable!));
  const fromLevel = nodes.find(n => n.id === prevTable)?.data.level;
  edges.push(createTableEdge(fromLevel, level!, prevTable!, table, right!, false, _table.source));
  lineage?.forEach(e => {
    const src = getColumnId(e.source[0], e.source[1]);
    const dst = getColumnId(e.target[0], e.target[1]);
    const viewsCodes: Record<string, [string, string][]> = {};
    if (right) {
      lineage
        .filter(e1 => e1.target.join("/") === e.target.join("/"))
        .forEach(e1 => {
          viewsCodes[e1.source.join("/")] = e1.viewsCode || [];
        });
    }
    if (right) {
      if (e.target[0] !== table) return;
      nodes.push(
        createColumnNode(e.target[0], e.target[1], e.viewsType, viewsCodes, _table.nodeType)
      );
      edges.push(createColumnEdge(src, dst, level! - 1, level!, e.type, edgeVisibility));
    } else {
      if (e.source[0] !== table) return;
      nodes.push(
        createColumnNode(e.source[0], e.source[1], e.viewsType, viewsCodes, _table.nodeType)
      );
      edges.push(createColumnEdge(src, dst, level!, level! + 1, e.type, edgeVisibility));
    }
  });

  if (tables!.every(t => !!nodes.find(n => n.id === t.table))) {
    const seeMoreNodeId = getSeeMoreId(prevTable!, right!);
    const seeMoreEdgeId = right ? `${prevTable}-${seeMoreNodeId}` : `${seeMoreNodeId}-${prevTable}`;
    deleteIfExists(nodes, seeMoreNodeId);
    deleteIfExists(edges, seeMoreEdgeId);
    return true;
  }
  return false;
};

export const calculateNodeCount = async (
  nodes: Node[],
  edges: Edge[],
  selectedTable: string,
  leftExpansion: number,
  rightExpansion: number
): Promise<number> => {
  if (!selectedTable) return 0;
  const selectedTableData = nodes.find(n => n.id === selectedTable)?.data;
  if (!selectedTableData) return 0;
  const { level } = selectedTableData;
  const startingNodesNum = nodes.length;
  const [newNodes] = await expandTableLineageLevelWise(
    nodes,
    edges,
    selectedTable,
    level - leftExpansion,
    level + rightExpansion,
    Infinity
  );
  return newNodes.length - startingNodesNum;
};

const getTracedNode = (node: Node, nodes: Node[], edges: Edge[], isIncomer: boolean) => {
  if (!isNode(node)) {
    return { nodes: [], edgeIds: [] };
  }

  const tracedEdges = edges.filter(e => {
    const id = isIncomer ? e.target : e.source;

    return id === node.id;
  });

  return {
    nodes: nodes.filter(n => tracedEdges.find(e => e.source === n.id || e.target === n.id)),
    edgeIds: tracedEdges.map(e => getColumnEdgeId(e.source, e.target)),
  };
};

export const getAllTracedNodes = (
  node: Node,
  nodes: Node[],
  edges: Edge[],
  prevTraced = [] as Node[],
  isIncomer: boolean
) => {
  const { nodes: tracedNodes, edgeIds } = getTracedNode(node, nodes, edges, isIncomer);

  return tracedNodes.reduce(
    (memo, tracedNode) => {
      memo.nodes.push(tracedNode);
      memo.edges = Array.from(new Set([...memo.edges, ...edgeIds]));

      if (prevTraced.findIndex(n => n.id === tracedNode.id) === -1) {
        prevTraced.push(tracedNode);

        const { nodes: _nodes, edges: _edgeIds } = getAllTracedNodes(
          tracedNode,
          nodes,
          edges,
          prevTraced,
          isIncomer
        );
        _nodes.forEach(foundNode => {
          memo.nodes.push(foundNode);

          if (prevTraced.findIndex(n => n.id === foundNode.id) === -1) {
            prevTraced.push(foundNode);
          }
        });
        memo.edges = Array.from(new Set([...memo.edges, ..._edgeIds]));
      }

      return memo;
    },
    { nodes: [] as Node[], edges: [] as string[] }
  );
};

export const highlightColumnConnections = (node: Node, flow: ReactFlowInstance) => {
  const nodes = flow.getNodes().filter(n => isColumn(n));
  const edges = flow.getEdges();

  // TODO check why styles are not applied when nodes and edges are directly from flow instance
  nodes.forEach(n => {
    const node = flow.getNode(n.id);
    if (node) {
      applyNodeStyling(node, false);
    }
  });
  edges.forEach(e => {
    const edge = flow.getEdge(e.id);
    if (edge) {
      // Ref (ERD overlay) edges are not part of column-level lineage, so a
      // CLL trace must not hide them. The `applyEdgeStyling` guard only
      // blocks style changes — it cannot undo a `hidden` write — so the
      // guard has to live here, before the assignment.
      if (!isRefEdge(edge)) edge.hidden = true;
      applyEdgeStyling(edge, false);
    }
  });

  const incomingNodes = getAllTracedNodes(node, nodes, edges, [], true);
  const outgoingNodes = getAllTracedNodes(node, nodes, edges, [], false);

  [incomingNodes, outgoingNodes].forEach(({ nodes: tracedNodes, edges }) => {
    tracedNodes.forEach(n => {
      const node = flow.getNode(n.id);
      if (node) {
        applyNodeStyling(node, true);
      }
    });
    edges.forEach(edgeId => {
      const edge = flow.getEdge(edgeId);
      if (edge) {
        edge.hidden = false;
        applyEdgeStyling(edge, true);
      }
    });
  });
};
